import { Api } from "services/api/api.service";
import { ok } from "utils/result";
import type { Result } from "utils/result";
import {
  loadStoredPreferencesWithRecovery,
  parseStoredPreferences,
} from "web-apps/company/stores/preferences.storage.logic";
import type { TimeReportsV2Dispatch } from "./timeReportsV2Store";
import {
  timeReportsV2InitState,
  type TimeReportsV2DisplayOption,
  type TimeReportsV2GroupByOption,
  type TimeReportsV2ListQuery,
  type TimeReportsV2Preferences,
  type TimeReportsV2StaffRegisterQuery,
  type TimeReportsV2SummaryQuery,
} from "./timeReportsV2Store.reducer";

interface FetchStaffRegisterParams {
  readonly append: boolean;
  readonly companyId: number;
  readonly page: number;
  readonly query: TimeReportsV2StaffRegisterQuery;
}

let listRequestCounter = 0;
let summaryRequestCounter = 0;
let countRequestCounter = 0;
let staffRegisterRequestCounter = 0;
const inFlightListRequests = new Map<string, Promise<void>>();
const inFlightSummaryRequests = new Map<string, Promise<void>>();

const timeReportsV2PreferencesStorageKey = (companyId: number): string =>
  `PERSISTED::TimeReportsV2Preferences::${companyId}`;

const hasStringGroupByOption = (
  value: object,
): value is { groupByOption: string } =>
  typeof Reflect.get(value, "groupByOption") === "string";

const isGroupByOption = (value: string): value is TimeReportsV2GroupByOption =>
  value === "person" || value === "team";

interface StoredTimeReportsV2Preferences {
  readonly displayOption?: unknown;
  readonly groupByOption: TimeReportsV2GroupByOption;
}

const isTimeReportsV2Preferences = (
  value: unknown,
): value is StoredTimeReportsV2Preferences => {
  if (typeof value !== "object" || value === null) return false;
  if (!hasStringGroupByOption(value)) return false;
  return isGroupByOption(value.groupByOption);
};

const parseTimeReportsV2Preferences = (
  raw: string,
): Result<TimeReportsV2Preferences, "invalid-preferences"> => {
  const parsed = parseStoredPreferences(raw, isTimeReportsV2Preferences);
  if (!parsed.ok) return parsed;
  return ok({
    displayOption:
      parsed.value.displayOption === "detailed" ? "detailed" : "simple",
    groupByOption: parsed.value.groupByOption,
  });
};

const serializeNumberList = (values: number[]): string => values.join(",");

const toListRequestKey = (
  companyId: number,
  query: TimeReportsV2ListQuery,
  page: number,
  append: boolean,
): string => {
  const filtersKey = query.filters?.join(",") ?? "";
  return [
    companyId,
    query.fromDate,
    query.toDate,
    page,
    query.pageSize,
    query.sortBy,
    filtersKey,
    serializeNumberList(query.workGroupIds),
    serializeNumberList(query.workerIds),
    append ? "append" : "replace",
  ].join("|");
};

const toSummaryRequestKey = (
  companyId: number,
  query: TimeReportsV2SummaryQuery,
): string => {
  return [
    companyId,
    query.fromDate,
    query.toDate,
    serializeNumberList(query.workGroupIds),
    serializeNumberList(query.workerIds),
  ].join("|");
};

const fetchList =
  (dispatch: TimeReportsV2Dispatch) =>
  async (
    companyId: number,
    query: TimeReportsV2ListQuery,
    page: number,
    append: boolean,
  ): Promise<void> => {
    const requestKey = toListRequestKey(companyId, query, page, append);
    const inFlightRequest = inFlightListRequests.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestId = (listRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_LIST_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_LIST_LOADING",
      payload: { value: page === 0, requestId },
    });
    dispatch({
      type: "SET_LIST_LOADING_MORE",
      payload: { value: page > 0, requestId },
    });
    dispatch({ type: "SET_LIST_REQUEST_ERROR", payload: null, requestId });
    if (page === 0 && !append) {
      dispatch({
        type: "SET_LIST",
        payload: {
          data: null,
          requestId,
          append: false,
          page: 0,
          pageSize: query.pageSize,
        },
      });
    }

    const request = (async (): Promise<void> => {
      try {
        const response = await Api().company.timeReports.getTimeReportsV2(
          companyId,
          query.fromDate,
          query.toDate,
          page,
          query.pageSize,
          query.sortBy,
          query.filters,
          query.workGroupIds,
          query.workerIds,
        );
        dispatch({
          type: "SET_LIST",
          payload: {
            data: response.data,
            requestId,
            append,
            page,
            pageSize: query.pageSize,
          },
        });
      } catch (error) {
        dispatch({
          type: "SET_LIST_REQUEST_ERROR",
          payload: {
            message: "Could not fetch time reports",
            err: error instanceof Error ? error : new Error("Unknown error"),
          },
          requestId,
        });
      } finally {
        dispatch({
          type: "SET_LIST_LOADING",
          payload: { value: false, requestId },
        });
        dispatch({
          type: "SET_LIST_LOADING_MORE",
          payload: { value: false, requestId },
        });
        inFlightListRequests.delete(requestKey);
      }
    })();

    inFlightListRequests.set(requestKey, request);
    await request;
  };

const fetchSummary =
  (dispatch: TimeReportsV2Dispatch) =>
  async (
    companyId: number,
    query: TimeReportsV2SummaryQuery,
  ): Promise<void> => {
    const requestKey = toSummaryRequestKey(companyId, query);
    const inFlightRequest = inFlightSummaryRequests.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestId = (summaryRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_SUMMARY_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_SUMMARY_LOADING",
      payload: { value: true, requestId },
    });
    dispatch({ type: "SET_SUMMARY_REQUEST_ERROR", payload: null });

    const request = (async (): Promise<void> => {
      try {
        const response =
          await Api().company.timeReports.getTimeReportsV2Summary(
            companyId,
            query.fromDate,
            query.toDate,
            query.workGroupIds,
            query.workerIds,
          );
        dispatch({
          type: "SET_SUMMARY",
          payload: { data: response.data, requestId },
        });
      } catch (error) {
        dispatch({
          type: "SET_SUMMARY_REQUEST_ERROR",
          payload: {
            message: "Could not refresh summary",
            err: error instanceof Error ? error : new Error("Unknown error"),
          },
        });
      } finally {
        dispatch({
          type: "SET_SUMMARY_LOADING",
          payload: { value: false, requestId },
        });
        inFlightSummaryRequests.delete(requestKey);
      }
    })();

    inFlightSummaryRequests.set(requestKey, request);
    await request;
  };

const refreshCount =
  (dispatch: TimeReportsV2Dispatch) =>
  async (companyId: number): Promise<void> => {
    const requestId = (countRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_COUNT_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_COUNT_LOADING",
      payload: { value: true, requestId },
    });
    dispatch({ type: "SET_COUNT_REQUEST_ERROR", payload: null });

    try {
      const response =
        await Api().company.timeReports.getTimeReportV2Count(companyId);
      dispatch({
        type: "SET_COUNT",
        payload: { value: response.data.value, requestId },
      });
    } catch (error) {
      dispatch({
        type: "SET_COUNT_REQUEST_ERROR",
        payload: {
          message: "Could not refresh count",
          err: error instanceof Error ? error : new Error("Unknown error"),
        },
      });
    }

    dispatch({
      type: "SET_COUNT_LOADING",
      payload: { value: false, requestId },
    });
  };

const fetchStaffRegister =
  (dispatch: TimeReportsV2Dispatch) =>
  async (params: FetchStaffRegisterParams): Promise<void> => {
    const requestId = (staffRegisterRequestCounter += 1);
    dispatch({
      type: "SET_ACTIVE_STAFF_REGISTER_REQUEST_ID",
      payload: requestId,
    });
    dispatch({
      type: "SET_STAFF_REGISTER_LOADING",
      payload: { value: params.page === 0 && !params.append, requestId },
    });
    dispatch({
      type: "SET_STAFF_REGISTER_LOADING_MORE",
      payload: { value: params.append, requestId },
    });
    dispatch({
      type: "SET_STAFF_REGISTER_REQUEST_ERROR",
      payload: { value: null, requestId },
    });
    dispatch({
      type: "SET_STAFF_REGISTER_QUERY",
      payload: params.query,
    });
    if (params.page === 0 && !params.append) {
      dispatch({
        type: "SET_STAFF_REGISTER",
        payload: {
          append: false,
          data: null,
          page: params.page,
          pageSize: params.query.pageSize,
          requestId,
        },
      });
    }

    const request = (async (): Promise<void> => {
      try {
        const response = await Api().company.timeReports.getStaffRegister({
          companyId: params.companyId,
          fromDate: params.query.fromDate,
          page: params.page,
          pageSize: params.query.pageSize,
          toDate: params.query.toDate,
        });
        dispatch({
          type: "SET_STAFF_REGISTER",
          payload: {
            append: params.append,
            data: response.data,
            page: params.page,
            pageSize: params.query.pageSize,
            requestId,
          },
        });
      } catch (error) {
        dispatch({
          type: "SET_STAFF_REGISTER_REQUEST_ERROR",
          payload: {
            value: {
              message: "Could not fetch staff register",
              err: error instanceof Error ? error : new Error("Unknown error"),
            },
            requestId,
          },
        });
      } finally {
        dispatch({
          type: "SET_STAFF_REGISTER_LOADING",
          payload: { value: false, requestId },
        });
        dispatch({
          type: "SET_STAFF_REGISTER_LOADING_MORE",
          payload: { value: false, requestId },
        });
      }
    })();

    await request;
  };

interface RefreshLoadedListParams {
  readonly companyId: number;
  readonly lastPage: number;
  readonly query: TimeReportsV2ListQuery;
}

// Keep editors mounted while blocking pagination until all loaded pages agree.
const refreshLoadedList =
  (dispatch: TimeReportsV2Dispatch) =>
  async ({
    companyId,
    lastPage,
    query,
  }: RefreshLoadedListParams): Promise<void> => {
    const requestId = (listRequestCounter += 1);
    dispatch({ type: "SET_ACTIVE_LIST_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_LIST_REFRESHING",
      payload: { value: true, requestId },
    });
    dispatch({
      type: "SET_LIST_LOADING_MORE",
      payload: { value: true, requestId },
    });
    dispatch({ type: "SET_LIST_REQUEST_ERROR", payload: null, requestId });
    try {
      const responses = [];
      for (let page = 0; page <= lastPage; page += 1) {
        responses.push(
          await Api().company.timeReports.getTimeReportsV2(
            companyId,
            query.fromDate,
            query.toDate,
            page,
            query.pageSize,
            query.sortBy,
            query.filters,
            query.workGroupIds,
            query.workerIds,
          ),
        );
      }
      dispatch({
        type: "SET_LIST",
        payload: {
          data: {
            items: responses.flatMap((response) => response.data.items),
            total: responses[0].data.total,
          },
          requestId,
          append: false,
          page: lastPage,
          pageSize: query.pageSize,
        },
      });
    } catch (error) {
      dispatch({
        type: "SET_LIST_REQUEST_ERROR",
        payload: {
          message: "Could not refresh time reports",
          err: error instanceof Error ? error : new Error("Unknown error"),
        },
        requestId,
      });
    } finally {
      dispatch({
        type: "SET_LIST_REFRESHING",
        payload: { value: false, requestId },
      });
      dispatch({
        type: "SET_LIST_LOADING",
        payload: { value: false, requestId },
      });
      dispatch({
        type: "SET_LIST_LOADING_MORE",
        payload: { value: false, requestId },
      });
    }
  };

const loadTimeReportsV2Preferences =
  (dispatch: TimeReportsV2Dispatch) =>
  (companyId: number): void => {
    dispatch({ type: "SET_PREFERENCES_REQUEST_ERROR", payload: null });
    loadStoredPreferencesWithRecovery({
      companyId,
      storageKey: timeReportsV2PreferencesStorageKey,
      fallback: timeReportsV2InitState.preferences,
      parse: parseTimeReportsV2Preferences,
      setState: (next: TimeReportsV2Preferences) => {
        dispatch({ type: "SET_PREFERENCES", payload: next });
      },
      setError: (error: Error) => {
        dispatch({
          type: "SET_PREFERENCES_REQUEST_ERROR",
          payload: {
            message: "Could not load preferences",
            err: error,
          },
        });
      },
    });
  };

const persistTimeReportsV2Preferences =
  (dispatch: TimeReportsV2Dispatch) =>
  (companyId: number, preferences: TimeReportsV2Preferences): void => {
    dispatch({ type: "SET_PREFERENCES_REQUEST_ERROR", payload: null });
    try {
      localStorage.setItem(
        timeReportsV2PreferencesStorageKey(companyId),
        JSON.stringify(preferences),
      );
    } catch {
      dispatch({
        type: "SET_PREFERENCES_REQUEST_ERROR",
        payload: {
          message: "Could not save preferences",
          err: new Error("Could not persist preferences to localStorage"),
        },
      });
      return;
    }
  };

interface TimeReportsV2RefreshQueries {
  readonly listQuery: TimeReportsV2ListQuery;
  readonly summaryQuery: TimeReportsV2SummaryQuery;
}

export interface TimeReportsV2ActionCreators {
  readonly fetchList: ReturnType<typeof fetchList>;
  readonly fetchStaffRegister: ReturnType<typeof fetchStaffRegister>;
  readonly fetchSummary: ReturnType<typeof fetchSummary>;
  readonly loadTimeReportsV2Preferences: ReturnType<
    typeof loadTimeReportsV2Preferences
  >;
  readonly persistTimeReportsV2Preferences: ReturnType<
    typeof persistTimeReportsV2Preferences
  >;
  readonly refreshAll: (
    companyId: number,
    queries: TimeReportsV2RefreshQueries,
  ) => Promise<void>;
  readonly refreshCount: ReturnType<typeof refreshCount>;
  readonly refreshLoadedList: ReturnType<typeof refreshLoadedList>;
  readonly resetList: () => void;
  readonly setDisplayOption: (
    displayOption: TimeReportsV2DisplayOption,
  ) => void;
  readonly setGroupByOption: (
    groupByOption: TimeReportsV2GroupByOption,
  ) => void;
  readonly setListQuery: (query: TimeReportsV2ListQuery | null) => void;
  readonly setPreferences: (preferences: TimeReportsV2Preferences) => void;
  readonly setStaffRegisterQuery: (
    query: TimeReportsV2StaffRegisterQuery | null,
  ) => void;
  readonly setSummaryQuery: (query: TimeReportsV2SummaryQuery | null) => void;
}

export const timeReportsV2Actions = (
  dispatch: TimeReportsV2Dispatch,
): TimeReportsV2ActionCreators => ({
  fetchList: fetchList(dispatch),
  fetchSummary: fetchSummary(dispatch),
  refreshCount: refreshCount(dispatch),
  fetchStaffRegister: fetchStaffRegister(dispatch),
  resetList: () => dispatch({ type: "RESET_LIST" }),
  setListQuery: (query: TimeReportsV2ListQuery | null) =>
    dispatch({ type: "SET_LIST_QUERY", payload: query }),
  setSummaryQuery: (query: TimeReportsV2SummaryQuery | null) =>
    dispatch({ type: "SET_SUMMARY_QUERY", payload: query }),
  setStaffRegisterQuery: (query: TimeReportsV2StaffRegisterQuery | null) =>
    dispatch({ type: "SET_STAFF_REGISTER_QUERY", payload: query }),
  setPreferences: (preferences: TimeReportsV2Preferences) =>
    dispatch({ type: "SET_PREFERENCES", payload: preferences }),
  setGroupByOption: (groupByOption: TimeReportsV2GroupByOption) =>
    dispatch({
      type: "SET_GROUP_BY_OPTION",
      payload: groupByOption,
    }),
  setDisplayOption: (displayOption: TimeReportsV2DisplayOption): void =>
    dispatch({ type: "SET_DISPLAY_OPTION", payload: displayOption }),
  refreshLoadedList: refreshLoadedList(dispatch),
  loadTimeReportsV2Preferences: loadTimeReportsV2Preferences(dispatch),
  persistTimeReportsV2Preferences: persistTimeReportsV2Preferences(dispatch),
  refreshAll: async (
    companyId: number,
    queries: TimeReportsV2RefreshQueries,
  ): Promise<void> => {
    dispatch({ type: "SET_LIST_QUERY", payload: queries.listQuery });
    dispatch({ type: "SET_SUMMARY_QUERY", payload: queries.summaryQuery });
    await Promise.all([
      fetchList(dispatch)(companyId, queries.listQuery, 0, false),
      fetchSummary(dispatch)(companyId, queries.summaryQuery),
      refreshCount(dispatch)(companyId),
    ]);
  },
});
